/** What a request resolves to: the raw response, its text body and the body
 *  parsed as JSON (undefined when the body is empty). */
export interface FetchResult<T = unknown> {
  response: Response
  status: number
  ok: boolean
  body: string
  data: T | undefined
}

// custom exception type for when requests fail
export class HttpError<E = unknown> extends Error {
  response: Response
  status: number
  body: string
  data: E | undefined
  constructor(context: FetchResult<E>, message?: string) {
    super(message || context.response.statusText)
    this.response = context.response
    this.status = context.status
    this.body = context.body
    this.data = context.data
    this.name = "HttpError"
  }
}

/** The `error` message a failed request's JSON body carries (`{ error }`, as
 *  our Netlify functions send), if any. */
export function httpErrorMessage(err: unknown): string | undefined {
  if (!(err instanceof HttpError)) return undefined
  const data: unknown = err.data
  if (typeof data !== "object" || data === null || !("error" in data)) return undefined
  return typeof data.error === "string" ? data.error : undefined
}

// custom exception for invalid response errors
class InvalidResponseError extends Error {
  body: string
  constructor(context: { body: string }, message: string) {
    super(message)
    this.body = context.body
    this.name = "InvalidResponseError"
  }
}

type HeaderMap = Record<string, string>

export interface ClientDefaults {
  /** Prefix for every request url that isn't absolute. */
  baseURL?: string
  headers?: HeaderMap
  /** Headers resolved per request, e.g. a token that can change. */
  getHeaders?: () => HeaderMap | Promise<HeaderMap>
}

export interface RequestOptions extends Omit<RequestInit, "headers"> {
  headers?: HeaderMap
  params?: ConstructorParameters<typeof URLSearchParams>[0]
}

// Wrapper around the brower's native fetch that provides nice functionality
// similar to axios. Allows creating a client with shared presets, using
// specific HTTP methods via dedicated method calls, defaults to sending
// & receiving JSON, etc.
//
// example:
//  client = fetchClient(baseURL: 'https://myfoo/v1', headers: { my_header: 'foo' })
//  response = client.get("people", { params: { limit: 20 } })
export function fetchClient(defaults: ClientDefaults = {}) {
  // if a baseUrl is specified, use it as prefix for all requests
  const baseURL = defaults.baseURL
  const getHeaders = defaults.getHeaders

  // send json content-type header unless overridden
  const defaultHeaders: HeaderMap = {
    "Content-Type": "application/json",
    ...defaults.headers,
  }

  // build full request url, incorporating baseURL and query params
  const buildUrl = (
    baseUrl: string | undefined,
    url: string,
    params: RequestOptions["params"] = undefined
  ) => {
    let queryString = ""
    if (params) {
      queryString = "?" + new URLSearchParams(params).toString()
    }
    if (!baseUrl || url.includes("://")) return `${url}${queryString}`
    return `${baseUrl}${url}${queryString}`
  }

  // parse out text body and json data if available
  const parseBodyAndData = async <T>(response: Response) => {
    let data: T | undefined = undefined
    const body = await response.text()
    if (body) {
      try {
        data = JSON.parse(body) as T
      } catch {
        throw new InvalidResponseError({ body }, `Response is not valid JSON, see body`)
      }
    }
    return { body, data }
  }

  // base method for requests with arguments similar to fetch. `T` is the JSON
  // body the caller expects; it is not validated.
  const request = async <T = unknown>(
    url: string,
    options: RequestOptions = {}
  ): Promise<FetchResult<T>> => {
    const { params, ...init } = options
    const fullUrl = buildUrl(baseURL, url, params)
    const dynamicHeaders = getHeaders ? await getHeaders() : {}
    const headers = { ...defaultHeaders, ...dynamicHeaders, ...options.headers }
    const requestOptions = { ...init, headers }

    return fetch(fullUrl, requestOptions).then(async (response) => {
      const status = response.status
      const ok = response.ok
      const { body, data } = await parseBodyAndData<T>(response)
      const context = { response, body, data, status, ok }
      if (ok) {
        return context
      } else {
        throw new HttpError(context, `Request failed with status code ${status}`)
      }
    })
  }

  // helper methods for making use of a given HTTP method easier

  const get = <T = unknown>(url: string, options: RequestOptions = {}) => {
    const opts = { ...options, method: "GET" }
    return request<T>(url, opts)
  }

  const post = <T = unknown>(
    url: string,
    data: unknown = undefined,
    options: RequestOptions = {}
  ) => {
    const opts: RequestOptions = { ...options, method: "POST" }
    if (data !== undefined) {
      const jsonBody = JSON.stringify(data)
      opts.body = jsonBody
    }
    return request<T>(url, opts)
  }

  return {
    request,
    get,
    post,
  }
}
